/**
 * Token Manager - Sprint 1b
 *
 * Manages OAuth token lifecycle: storage, retrieval, validation, and refresh.
 * Emits events for token state changes.
 *
 * @see docs/sprints/ownyou-sprint1b-spec.md
 */
import { TOKEN_REFRESH_MARGIN_MS } from './types';
/**
 * Token Manager
 *
 * Provides token lifecycle management including:
 * - Storage abstraction (browser localStorage, OS keychain, or in-memory)
 * - Token validity checking
 * - Automatic refresh detection
 * - Event emission for token state changes
 */
export class TokenManager {
    storage;
    eventHandlers = new Map();
    constructor(storage) {
        this.storage = storage;
    }
    /**
     * Store tokens for a user and provider
     */
    async storeTokens(userId, provider, tokens) {
        await this.storage.store(userId, provider, tokens);
    }
    /**
     * Get stored tokens for a user and provider
     */
    async getTokens(userId, provider) {
        return await this.storage.get(userId, provider);
    }
    /**
     * Delete stored tokens for a user and provider
     */
    async deleteTokens(userId, provider) {
        await this.storage.delete(userId, provider);
    }
    /**
     * Check if tokens exist and are not expired
     */
    async isTokenValid(userId, provider) {
        const tokens = await this.storage.get(userId, provider);
        if (!tokens) {
            return false;
        }
        return tokens.expiresAt > Date.now();
    }
    /**
     * Check if tokens need to be refreshed
     * Returns true if tokens are expired or expiring soon (within refresh margin)
     */
    async needsRefresh(userId, provider) {
        const tokens = await this.storage.get(userId, provider);
        if (!tokens) {
            return false;
        }
        const expiresIn = tokens.expiresAt - Date.now();
        return expiresIn < TOKEN_REFRESH_MARGIN_MS;
    }
    /**
     * Check token expiry and emit events if needed
     */
    async checkTokenExpiry(userId, provider) {
        const tokens = await this.storage.get(userId, provider);
        if (!tokens) {
            return;
        }
        const expiresIn = tokens.expiresAt - Date.now();
        if (expiresIn <= 0) {
            // Token has expired
            return;
        }
        if (expiresIn < TOKEN_REFRESH_MARGIN_MS) {
            // Token is expiring soon, emit event
            this.emit('onTokenExpiring', provider, expiresIn);
        }
    }
    /**
     * Subscribe to token manager events
     *
     * @param event - Event name
     * @param handler - Event handler function
     * @returns Unsubscribe function
     */
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, new Set());
        }
        const handlers = this.eventHandlers.get(event);
        handlers.add(handler);
        // Return unsubscribe function
        return () => {
            handlers.delete(handler);
        };
    }
    /**
     * Emit an event
     */
    emit(event, ...args) {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.forEach((handler) => {
                handler(...args);
            });
        }
    }
    /**
     * Notify that tokens were refreshed
     */
    notifyTokenRefreshed(provider, tokens) {
        this.emit('onTokenRefreshed', provider, tokens);
    }
    /**
     * Notify that token refresh failed
     */
    notifyTokenRefreshFailed(provider, error) {
        this.emit('onTokenRefreshFailed', provider, error);
    }
}
//# sourceMappingURL=token-manager.js.map